import { Link } from "react-router";

import { FingerprintIcon, ImagesIcon, UserIcon } from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { EnrichmentSummary } from "@/components/enrichment/enrichment-summary";
import { ShowViewHeader } from "@/components/refine-ui/views/show-view";
import {
  Chips,
  DetailField,
  DetailGrid,
  DetailSection,
  MediaGallery,
  MetaDot,
  RawJsonCard,
  ShowHero,
  SystemSection,
  useShowRecord,
} from "@/components/show";
import { Badge } from "@/components/ui/badge";
import {
  PersonViewType,
  personViewSchema,
} from "@/pages/people/schemas/person.view";
import { EntityTinaTrace } from "@/pages/tina/components/entity-tina-trace";
import { ReEnrichButton } from "@/pages/tina/components/re-enrich-button";

export const PeopleShow = () => {
  const { record, raw } = useShowRecord<PersonViewType>(
    personViewSchema,
    (r) => `Person | ${r.name ?? r.id}`,
  );

  return (
    <div className="p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <ShowViewHeader />

        {record && (
          <>
            <ShowHero
              round
              poster={record.media?.photos?.[0]}
              eyebrow={record.knownForDepartment}
              title={record.name ?? "Unknown"}
              meta={
                <>
                  {typeof record.followers === "number" && (
                    <span>{record.followers.toLocaleString()} followers</span>
                  )}
                  {typeof record.popularity === "number" && (
                    <>
                      <MetaDot />
                      <span>Popularity {record.popularity.toFixed(1)}</span>
                    </>
                  )}
                </>
              }
              badges={
                <>
                  {record.status && (
                    <Badge variant="default">{record.status}</Badge>
                  )}
                  {record.knownForDepartment && (
                    <Badge variant="secondary">
                      {record.knownForDepartment}
                    </Badge>
                  )}
                  {record.hide && <Badge variant="secondary">Hidden</Badge>}
                </>
              }
              description={record.about}
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <DetailSection
                title="Profile"
                icon={<UserIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="Name" value={record.name} />
                  <DetailField
                    label="Known for"
                    value={record.knownForDepartment}
                  />
                  <DetailField
                    label="Followers"
                    value={record.followers?.toLocaleString()}
                  />
                  <DetailField
                    label="Popularity"
                    value={record.popularity?.toFixed(2)}
                  />
                  <DetailField label="Aliases" full>
                    <Chips items={record.aliases} variant="outline" />
                  </DetailField>
                  <DetailField label="Name per language" full>
                    <Chips
                      items={Object.entries(record.translations ?? {})
                        .filter(([, value]) => value?.name)
                        .map(([locale, value]) => `${locale}: ${value?.name}`)}
                      variant="outline"
                    />
                  </DetailField>
                </DetailGrid>
              </DetailSection>

              <DetailSection
                title="Identifiers"
                icon={<FingerprintIcon className="size-4" />}
              >
                <DetailGrid>
                  <DetailField label="ID" mono>
                    <span className="inline-flex items-center gap-1">
                      {record.id}
                      <CopyButton text={record.id} message="Copied person ID" />
                    </span>
                  </DetailField>
                  <DetailField label="Source" value={record.metadata?.source} />
                  <DetailField
                    label="TMDB ID"
                    value={record.metadata?.tmdbId?.toString()}
                    mono
                  />
                  <DetailField
                    label="IMDb ID"
                    value={record.metadata?.imdbId}
                    mono
                  />
                  <DetailField label="Linked artist">
                    {record.artistId ? (
                      <Link
                        to={`/artists/edit/${record.artistId}`}
                        className="font-mono text-[13px] text-primary hover:underline"
                      >
                        {record.artistId}
                      </Link>
                    ) : undefined}
                  </DetailField>
                </DetailGrid>
              </DetailSection>
            </div>

            <EnrichmentSummary
              resource="people"
              id={record.id}
              status={record.enrichmentStatus}
              enrichment={record.enrichment}
              bordered
            />

            <DetailSection
              title="Media"
              icon={<ImagesIcon className="size-4" />}
            >
              <MediaGallery media={record.media} aspect="portrait" />
            </DetailSection>

            {/* Tina — provenance, and the only non-SQL way to re-queue this
                entity's enrichment. Unlike
                artist/venue/geodata/event this entity has no per-entity enrich
                trigger anywhere in IMS, so `POST /enrichments` is not a second
                path to the same thing — it is the only one. */}
            <div className="flex flex-col gap-4">
              <div className="flex justify-end">
                <ReEnrichButton entityType="person" entityId={record.id} />
              </div>
              <EntityTinaTrace
                entityType="person"
                entityId={record.id}
                metadata={record.metadata}
                enrichment={record.enrichment}
              />
            </div>

            <SystemSection record={record} />

            <RawJsonCard data={raw} />
          </>
        )}
      </div>
    </div>
  );
};
